import { BaseParser,
        DATA_TYPE_DATA,
        TYPE_PSSESSION,
        RE_TIME_HEADER_FIRST,
        RE_CURRENT_HEADER_FIRST,
        RE_VOLTAGE_HEADER_FIRST,
        RE_REVERSE_CURRENT_HEADER_FIRST,
        RE_STATE_HEADER_FIRST,
        RE_FREQUENCY_HEADER_FIRST,
        RE_IMPEDANCE_RE_HEADER_FIRST,
        RE_IMPEDANCE_IM_HEADER_FIRST,
       } from './baseExtractor.js';
import { ExtraConfigKey, NameExtraConfig } from '../commonPlotDefinitions.js'
import { getDateFromString } from '../parsers.js'
import { TXTExtractor } from './commaSepExtractor';

const RE_REVERSE_CURRENT = /rev[ers]*\s*cur/i;
const RE_DATE_CHARACTERS = /[-/:]/i;

const reCSVExt = RegExp('(.*)\\.csv$', 'i');
//const RE_DELTA_LINK_CSV = /(Nitrate Sensor)|(Voltage)|(Current)/i
const RE_ADDITIONAL_CSV_FORMAT = /(ampero)|(voltam)/i
const iconCSVFile = 'csv.png';


class CSVExtractor extends BaseParser {
  findSeparator(array) {
    if (array.length < 2) {
      return null;
    }
    const tryElements = [',', '\t'];
    //Try with each element
    for (let j = 0; j < tryElements.length; j++) {
      const symbol = tryElements[j];
      const commaValidation =
        array[0].split(symbol).length > 1 &&
        array[0].split(symbol).length === array[1].split(symbol).length &&
        (array.length === 2 ||
          array[0].split(symbol).length ===
            array[2].split(symbol).length);
      if (commaValidation) {
        return symbol;
      }
    }
    return null;
  }

  // Returns line number containing header end
  findHeaderEnd(array){
    for (let i=0;i<array.length;i++){
      if (array[i].includes('meas_header')){
        return i+1
      }
    }
    return -1
  }

  extractColumnCVS(warnings, dataSplit, splitStr, colHead, headersLine, footersLine = 0, isFloat = true, skipFirst = false) {
    const response = this.extractColumnCVSCommon(
        warnings,
        dataSplit,
        splitStr,
        colHead,
        headersLine,
        footersLine,
        isFloat,
        skipFirst,
    );
    return response.values;
  }

  extractColumnCVSDate(warnings, dataSplit, splitStr, colHead, headersLine, footersLine = 0, isFloat = true, skipFirst = false) {
    return this.extractColumnCVSCommon(
        warnings,
        dataSplit,
        splitStr,
        colHead,
        headersLine,
        footersLine,
        isFloat,
        skipFirst,
        true,
    );
}

  extractColumnCVSCommon(warnings, dataSplit, splitStr, colHead, headersLine, footersLine = 0, isFloat = true, skipFirst = false, allowTime = false) {
    //Split the header to get the titles
    const headerSplit = dataSplit[headersLine].split(splitStr);
    let indxCol = -1;
    let first = false;
    let initialDate = undefined;
    for (let j = 0; j < headerSplit.length; j++) {
        if (colHead.test(headerSplit[j])) {
            if (skipFirst && !first) {
                //If it skip the first validate if it has found before one
                first = true;
                continue;
            }
            indxCol = j;
            break;
        }
    }
    if (indxCol === -1) {
        return { values: null, initialDate: undefined };
    }
    const values = [];
    for (let j = headersLine + 1; j < dataSplit.length - footersLine; j++) {
        const linesplitted = dataSplit[j].split(splitStr);
        if (allowTime && RE_DATE_CHARACTERS.test(linesplitted[indxCol])) {
            const parseDate = getDateFromString(linesplitted[indxCol]);
            if (parseDate !== null) {
                if (typeof initialDate === 'undefined') {
                    initialDate = parseDate;
                    values.push(0);
                    continue;
                }
                const val_date =
                    (parseDate.getTime() - initialDate.getTime()) / 1000;
                values.push(val_date);
                continue;
            }
            if (initialDate !== null) {
                warnings.push(
                    'The column time includes some special characters but only in some elements. Please verify the file and homogenize the values, if the time is required.',
                );
                return { values: [], initialDate };
            }
        }
        const val = isFloat
            ? parseFloat(linesplitted[indxCol])
            : linesplitted[indxCol];
        values.push(val);
    }
    return { values, initialDate };
  }

  async _doParse(parent, fileName, data, report) {
    const returnValue = this.getNewReturnValue();
    const dataSplit = data
      // eslint-disable-next-line no-control-regex
      .replace(/[\x0A]/g, '\r')
      .replace('\n', '\r')
      .split('\r')
      .filter((line) => line !== '');
    const dataExtract = this.getDataFromFileName(fileName, reCSVExt); //Extract data from the file name
    const regexSeparator = RegExp('sep=(.)');
    const matchSep = regexSeparator.exec(dataSplit[0]);
    let separator = null;
    let headersLine = 0;
    let footersLine = 0;
    if (matchSep !== null) {
      separator = matchSep[1];
      headersLine = 1;
    }
    if (separator === null) {
      separator = this.findSeparator(dataSplit);
    }
    let reverseCurrent = false;
    if (separator === null) {
      //The file could be in the other format
      if (dataSplit.length >= 4 && RE_ADDITIONAL_CSV_FORMAT.test(dataSplit[3])) {
        separator = this.findSeparator(dataSplit.slice(5));
      }
      headersLine = 5;
      reverseCurrent = RE_REVERSE_CURRENT.test(dataSplit[3]);
    }
    // SenseItAll format
    if (separator === null){
      headersLine = this.findHeaderEnd(dataSplit)
      if (headersLine > 0) {
        separator = ',';
        footersLine = 1;
      }
    }

    if (separator === null) {
      returnValue.lastError = `The file '${fileName}' has a different format please submit your file with the request functionality in the Feedback form.`;
      return (await this.specializedCSV2(parent, fileName, data, report));
    }

    const dataCurrent = this.extractColumnCVS(
      returnValue.warnings,
      dataSplit,
      separator,
      RE_CURRENT_HEADER_FIRST,
      headersLine,
      footersLine,
    );
    const dataPotential = this.extractColumnCVS(
      returnValue.warnings,
      dataSplit,
      separator,
      RE_VOLTAGE_HEADER_FIRST,
      headersLine,
      footersLine,
    );
    const dataTime = this.extractColumnCVSDate(
      returnValue.warnings,
      dataSplit,
      separator,
      RE_TIME_HEADER_FIRST,
      headersLine,
      footersLine,
      undefined,
      undefined,
      true,
    );
    const dataState = this.extractColumnCVS(
        returnValue.warnings,
        dataSplit,
        separator,
        RE_STATE_HEADER_FIRST,
        headersLine,
        footersLine,
        false,
    );
    let dataReverseCurrent = null;
    if (reverseCurrent) {
        dataReverseCurrent = this.extractColumnCVS(
            returnValue.warnings,
            dataSplit,
            separator,
            RE_CURRENT_HEADER_FIRST,
            headersLine,
            footersLine,
            undefined,
            true,
        );
        if (dataReverseCurrent === null) {
          returnValue.warnings.push(`The file '${fileName}' should have reverse current and djuli could not find it. Please remove the file and try again if it is the case.`);
        }
    }else{
      dataReverseCurrent = this.extractColumnCVS(
        returnValue.warnings,
        dataSplit,
        separator,
        RE_REVERSE_CURRENT_HEADER_FIRST,
        headersLine,
        footersLine,
      );
    }
    let dataFrequency = this.extractColumnCVS(
      returnValue.warnings,
      dataSplit,
      separator,
      RE_FREQUENCY_HEADER_FIRST,
      headersLine,
      footersLine,
    );
    let dataImpedanceRE = this.extractColumnCVS(
      returnValue.warnings,
      dataSplit,
      separator,
      RE_IMPEDANCE_RE_HEADER_FIRST,
      headersLine,
      footersLine,
    );
    let dataImpedanceIM = this.extractColumnCVS(
      returnValue.warnings,
      dataSplit,
      separator,
      RE_IMPEDANCE_IM_HEADER_FIRST,
      headersLine,
      footersLine,
    );
    if((dataFrequency !== null || dataImpedanceRE !== null || dataImpedanceIM !== null) &&
        !(dataFrequency !== null && dataImpedanceRE !== null && dataImpedanceIM !== null)) {
      returnValue.warnings.push(`The file '${fileName}' contains incomplete EIS data. One column was not found. Please validate the file`);
      dataFrequency = null;
      dataImpedanceRE = null;
      dataImpedanceIM = null;
    }
    // EIS Data
    const isEISData = dataFrequency !== null && dataImpedanceRE !== null && dataImpedanceIM !== null;
    if(isEISData){
      if(dataCurrent !== null && dataPotential !== null){
        returnValue.lastError = `Djuli found EIS and Standard information in header on line ${headersLine} from file '${fileName}'. Please verify the file and try again.`;
        return returnValue;
      }
    }else{
      //Verify the data extracted
      if(dataTime.values !== null && dataCurrent === null && dataPotential === null){
        const txtExtractor = new TXTExtractor();
        const result = await txtExtractor._doParse(parent, fileName, data, report);
        result.warnings.push(`The file '${fileName}' only contains time data. This will be processed as 2D data.`);
        return result;
      }
      if (dataCurrent === null)
        returnValue.warnings.push(`The file '${fileName}' does not have a Current column defined`);
      if (dataPotential === null)
        returnValue.warnings.push(`The file '${fileName}' does not have a Potential column defined`);
      if (dataTime.values === null)
        returnValue.warnings.push(`The file '${fileName}' does not have a Time column defined`);
      //If there is any data for working, it discards the file
      if (dataCurrent === null && dataPotential === null && dataTime.values === null) {
          returnValue.lastError = `Djuli could not find electrochemical data in the file '${fileName}'. Please verify the file and try again.`;
          return returnValue;
      }
    }

    returnValue.processedData = [];
    if (dataState !== null) {
        const dictIndexProcessed = {};
        for (let j = 0; j < dataState.length; j++) {
            if (!(dataState[j] in dictIndexProcessed)) {
                //Create the value in the dictionary and add the object with the standard data
                dictIndexProcessed[dataState[j]] = returnValue.processedData.length;
                const newElement = {
                    title: `${dataExtract.title} ${dataState[j]}`,
                    group: dataExtract.group,
                    concUnit: dataExtract.concUnit,
                    fileName: fileName,
                    state: dataState[j],
                };
                if(isEISData){
                  newElement.impedance_re = []
                  newElement.impedance_im = []
                  newElement.frequency = []
                }else{
                  newElement.time = []
                  newElement.potential = []
                  newElement.current = []
                }
                if (typeof dataExtract.mapping !== 'undefined')
                    newElement.mapping = dataExtract.mapping;
                if (typeof dataExtract.scan !== 'undefined')
                    newElement.scan = dataExtract.scan;
                if(dataReverseCurrent !== null)
                  newElement.reverseCurrent = [];
                returnValue.processedData.push(newElement);
            }
            if (dataTime.values !== null)
                returnValue.processedData[dictIndexProcessed[dataState[j]]].time.push(dataTime.values[j]);
            if (dataPotential !== null)
                returnValue.processedData[dictIndexProcessed[dataState[j]]].potential.push(dataPotential[j]);
            if (dataCurrent !== null)
                returnValue.processedData[dictIndexProcessed[dataState[j]]].current.push(dataCurrent[j]);
            if (dataFrequency !== null)
              returnValue.processedData[dictIndexProcessed[dataState[j]]].frequency.push(dataFrequency[j]);
            if (dataImpedanceRE !== null)
                returnValue.processedData[dictIndexProcessed[dataState[j]]].impedance_re.push(dataImpedanceRE[j]);
            if (dataImpedanceIM !== null)
              returnValue.processedData[dictIndexProcessed[dataState[j]]].impedance_im.push(dataImpedanceIM[j]);
            if (typeof dataTime.initialDate !== 'undefined') {
                const d = dataTime.initialDate;
                const dateFormatted = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()} ${d.getHours()}:${d.getMinutes()}:${d.getSeconds()}`
                returnValue.processedData[dictIndexProcessed[dataState[j]]].date = dateFormatted;
            }
            if (dataReverseCurrent !== null) {
                report[ExtraConfigKey][NameExtraConfig.ReverseCurrentPresent] = true;
                returnValue.processedData[dictIndexProcessed[dataState[j]]].reverseCurrent.push(dataReverseCurrent[j]);
            }
        }
    } else {
        const extractedData = {
            title: dataExtract.title,
            group: dataExtract.group,
            concUnit: dataExtract.concUnit,
            time: dataTime.values,
            potential: dataPotential,
            current: dataCurrent,
            fileName: fileName,
            date: dataTime.initialDate || '',
        };
        if (typeof dataTime.initialDate !== 'undefined') {
            const d = dataTime.initialDate;
            const ye = new Intl.DateTimeFormat('en', {year: 'numeric'}).format(d);
            const mo = new Intl.DateTimeFormat('en', {month: 'short'}).format(d);
            const da = new Intl.DateTimeFormat('en', {day: '2-digit'}).format(d);
            extractedData.date = `${ye}-${mo}-${da} ${d.getHours()}:${d.getMinutes()}:${d.getSeconds()}`;
        }
        if (dataReverseCurrent !== null) {
            report[ExtraConfigKey][NameExtraConfig.ReverseCurrentPresent] = true;
            extractedData.reverseCurrent = dataReverseCurrent; //Save the Reverse Current from the file
        }
        returnValue.processedData = [extractedData];
    }
    returnValue.lastError = returnValue.processedData.length === 0 ? `File '${fileName}' is empty` : '';
    returnValue.dataType = DATA_TYPE_DATA;
    returnValue.type = TYPE_PSSESSION;
    returnValue.icon = iconCSVFile;
    returnValue.success = returnValue.processedData.length > 0;
    return returnValue;
  }

  async specializedCSV2(parent, fileName, data, report) {
    const returnValue = this.getNewReturnValue();
    try {
        let dataSplit = data
            // eslint-disable-next-line no-control-regex
            .replace(/[\x0A]/g, '\r')
            .replace('\n', '\r')
            .split('\r')
            .filter((line) => line !== '');
        const dataExtract = this.getDataFromFileName(fileName, reCSVExt); //Extract data from the file name
        //Try to find the special characteristics of the file
        const lineReaderProfile = dataSplit.findIndex((line) => line.includes('Reader Profile'));
        const dataLine = dataSplit.findIndex((line) => line.includes('Reader Data'));
        if (lineReaderProfile === -1 || dataLine === -1) {
            // Not matching the specialized structure, it doesn't have the labels required
            const txtExtractor = new TXTExtractor();
            const result = await txtExtractor._doParse(parent, fileName, data, report);
            result.warnings.push(`The file '${fileName}' does not match with a known structure. It was processed as 2D data.`);
            return result;
        }
        dataSplit = dataSplit.slice(dataLine + 1);
        const separator = ','; //findSeparator(dataSplit)
        const dataCurrent = 2;
        const dataPotential = 1;
        const dataTime = 0;
        returnValue.processedData = [];
        const definedStates = ['AD1', 'AD2', 'AD3', 'VD'];
        let indexStates = -1;
        for (let j = 0; j < dataSplit.length; j++) {
            const lineSplit = dataSplit[j].split(separator);
            const timeVal = parseFloat(lineSplit[dataTime]);
            const currVal = parseFloat(lineSplit[dataCurrent]);
            const poteVal = parseFloat(lineSplit[dataPotential]);
            if (timeVal === 0.0 || indexStates < 0) {
                indexStates++;
                const state = definedStates[indexStates];
                const newElement = {
                    title: `${dataExtract.title} ${state}`,
                    group: dataExtract.group,
                    concUnit: dataExtract.concUnit,
                    time: [],
                    potential: [],
                    current: [],
                    fileName: fileName,
                    state: state,
                };
                returnValue.processedData.push(newElement);
                if (typeof dataExtract.mapping !== 'undefined') {
                    newElement.mapping = dataExtract.mapping;
                }
                if (typeof dataExtract.scan !== 'undefined') {
                    newElement.scan = dataExtract.scan;
                }
            }
            returnValue.processedData[indexStates].time.push(timeVal);
            returnValue.processedData[indexStates].current.push(currVal);
            returnValue.processedData[indexStates].potential.push(poteVal);
        }
        returnValue.lastError = returnValue.processedData.length === 0 ? `File '${fileName}' is empty` : '';
        returnValue.dataType = DATA_TYPE_DATA;
        returnValue.type = TYPE_PSSESSION;
        returnValue.icon = iconCSVFile;
        returnValue.success = returnValue.processedData.length > 0;
        return returnValue;
    } catch (e) {
        returnValue.lastError = `Error the file "${fileName}" has a incorrect format. Error: ${e}`;
        return returnValue;
    }
  }
}

export{
  CSVExtractor,
};